import {
  EntityLinks,
  Goal,
  GoalGroupBy,
  GoalProgressCount,
  GoalRoadmapSortingOptions,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
  OrderDirection,
  UserSettings,
} from '@griply/types';

import { buildSortedGoalTree } from '../build-sorted-goal-tree';
import { getOrderedGoalIds } from '../get-ordered-goal-ids';
import { filterGoalsByType, goalOnlyTypes } from './filter-goals-by-type';
import { getGoalFolderChildren } from './get-goal-folder-children';
import { getLifeAreaGoalOrder } from './get-life-area-goal-order';
import { createGoalTreeIndex } from './goal-tree-index';

export type GetGoalsOverviewOrderOptions = {
  goals: Goal[];
  sortedLifeAreas: LifeArea[];
  links?: EntityLinks;
  groupBy?: GoalGroupBy;
  sortMode?: GoalSortingMode;
  sortDirection: OrderDirection;
  goalRoadmapSorting?: Partial<Record<GoalRoadmapSortingOptions, ID[]>>;
  goalGroupSorting?: UserSettings['goalGroupSorting'];
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
};

export type GoalsOverviewOrder = {
  // Canonical root order — one position per goal.
  ids: ID[];
  // Children per parent and, grouped by life area, per section; a goal under several holds a position under each.
  goalChildrenByParentId: Map<ID, ID[]>;
};

// The one order derivation for the goals overview (list, timeline, roadmap) on both platforms.
export const getGoalsOverviewOrder = ({
  goals,
  sortedLifeAreas,
  links,
  groupBy,
  sortMode,
  sortDirection,
  goalRoadmapSorting,
  goalGroupSorting,
  taskCounts,
  goalProgressCounts,
}: GetGoalsOverviewOrderOptions): GoalsOverviewOrder => {
  const sorted = buildSortedGoalTree({
    goals,
    sortedLifeAreas,
    goalGroupBy: groupBy,
    goalSortingMode: sortMode,
    goalRoadmapSorting,
    goalGroupSorting,
    taskCounts,
    goalProgressCounts,
    index: createGoalTreeIndex(goals, { links }),
    links,
  });

  const goalChildren = getGoalFolderChildren(goals, {
    links,
    sortMode,
    sortDirection,
    allGoals: goals,
    taskCounts,
    goalProgressCounts,
  });

  // `ids` holds one slot per goal, keyed on the legacy scalar; a goal in two areas needs one per section.
  const goalChildrenByParentId =
    groupBy === GroupByMode.LifeArea
      ? new Map([
          ...goalChildren,
          ...getLifeAreaGoalOrder({
            goals: filterGoalsByType(goals, goalOnlyTypes),
            allGoals: goals,
            lifeAreas: sortedLifeAreas,
            links,
            sortMode,
            sortDirection,
            goalGroupSorting: goalGroupSorting?.lifeArea?.sorting,
            taskCounts,
            goalProgressCounts,
          }),
        ])
      : goalChildren;

  return {
    ids: getOrderedGoalIds(sorted, sortMode, sortDirection),
    goalChildrenByParentId,
  };
};
